// src/pages/AdminUsers.jsx
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { ShieldCheck, ShieldOff, Ban, CheckCircle, RefreshCw, FlaskConical, ChevronLeft, ChevronRight } from 'lucide-react';
import { AuthContext } from '../AuthContext';
import { sanitizeImageUrl } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const PAGE_SIZE = 50;
const font = { mono: '"JetBrains Mono", monospace', condensed: '"Barlow Condensed", sans-serif', body: '"Barlow", sans-serif' };

const AdminUsers = () => {
  const { authToken } = useContext(AuthContext);
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [toast,   setToast]   = useState('');
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);

  const headers = { Authorization: `Bearer ${authToken}` };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const load = useCallback((p = page) => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/admin/users?page=${p}&limit=${PAGE_SIZE}`, { headers })
      .then(r => r.json())
      .then(d => {
        setUsers(d.users || []);
        setTotal(d.total || 0);
      })
      .catch(() => showToast('Failed to load users'))
      .finally(() => setLoading(false));
  }, [page, authToken]);

  useEffect(() => { load(page); }, [page]);

  const goTo = (p) => { setPage(p); };

  const action = async (url, msg) => {
    const res = await fetch(`${BACKEND_URL}${url}`, { method: 'POST', headers });
    if (res.ok) { showToast(msg); load(page); }
    else showToast('Action failed');
  };

  const filtered = users.filter(u =>
    u.login?.toLowerCase().includes(search.toLowerCase()) ||
    u.display_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const S = {
    page:     { padding: '2.5rem' },
    input:    { background: '#0d0d0d', border: '1px solid #1a1a1a', color: '#fff', padding: '0.55rem 0.9rem', fontFamily: font.mono, fontSize: '0.7rem', outline: 'none', width: 260 },
    th:       { fontFamily: font.mono, fontSize: '0.55rem', color: '#333', letterSpacing: '0.15em', textTransform: 'uppercase', padding: '0.6rem 0.9rem', textAlign: 'left', borderBottom: '1px solid #111', whiteSpace: 'nowrap' },
    td:       { padding: '0.7rem 0.9rem', borderBottom: '1px solid #0a0a0a', verticalAlign: 'middle' },
    btn:      { background: 'none', border: '1px solid #1a1a1a', color: '#666', padding: '0.28rem 0.65rem', fontFamily: font.mono, fontSize: '0.57rem', letterSpacing: '0.08em', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' },
    pill:     (bg, color) => ({ background: bg, color, fontFamily: font.mono, fontSize: '0.5rem', padding: '2px 6px', letterSpacing: '0.1em', marginRight: 3 }),
    pgBtn:    (active) => ({ background: active ? '#1a1a1a' : 'none', border: '1px solid #1a1a1a', color: active ? '#fff' : '#555', padding: '0.28rem 0.6rem', fontFamily: font.mono, fontSize: '0.57rem', cursor: 'pointer', minWidth: 32 }),
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const start = Math.max(1, page - 2);
    const end   = Math.min(totalPages, page + 2);
    if (start > 1) pages.push(1, '...');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) pages.push('...', totalPages);

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: '1.25rem' }}>
        <button style={S.pgBtn(false)} disabled={page === 1} onClick={() => goTo(page - 1)}><ChevronLeft size={11} /></button>
        {pages.map((p, i) =>
          p === '...'
            ? <span key={`e${i}`} style={{ fontFamily: font.mono, fontSize: '0.57rem', color: '#333', padding: '0 4px' }}>…</span>
            : <button key={p} style={S.pgBtn(p === page)} onClick={() => goTo(p)}>{p}</button>
        )}
        <button style={S.pgBtn(false)} disabled={page === totalPages} onClick={() => goTo(page + 1)}><ChevronRight size={11} /></button>
        <span style={{ fontFamily: font.mono, fontSize: '0.57rem', color: '#333', marginLeft: 8 }}>
          PAGE {page} / {totalPages}
        </span>
      </div>
    );
  };

  return (
    <div style={S.page}>
      <style>{`@keyframes fadein{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {toast && (
        <div style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', background: '#111', border: '1px solid #222', color: '#fff', fontFamily: font.mono, fontSize: '0.7rem', padding: '0.75rem 1.25rem', zIndex: 999, animation: 'fadein 0.2s ease' }}>
          {toast}
        </div>
      )}

      <div style={{ fontFamily: font.mono, fontSize: '0.58rem', color: '#333', letterSpacing: '0.2em', marginBottom: 6 }}>// MANAGE</div>
      <div style={{ fontFamily: font.condensed, fontWeight: 900, fontSize: '2.5rem', letterSpacing: '-0.02em', marginBottom: '1.5rem' }}>Users</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <input
          style={S.input}
          placeholder="Search username..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <span style={{ fontFamily: font.mono, fontSize: '0.62rem', color: '#333' }}>
          {total} TOTAL MEMBERS
        </span>
        <button onClick={() => load(page)} style={{ ...S.btn, marginLeft: 'auto' }}><RefreshCw size={11} /> REFRESH</button>
      </div>

      {loading ? (
        <p style={{ fontFamily: font.mono, fontSize: '0.68rem', color: '#333' }}>LOADING...</p>
      ) : (
        <>
          <div style={{ background: '#0a0a0a', border: '1px solid #111', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>{['User', 'Joined', 'Views', 'Status', 'Actions'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.twitch_id} style={{ opacity: u.is_banned ? 0.45 : 1 }}
                    onMouseEnter={e => e.currentTarget.style.background = '#0d0d0d'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={S.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <img src={sanitizeImageUrl(u.profile_image_url) ?? '/images/default-avatar.png'} alt="" style={{ width: 30, height: 30, borderRadius: '50%', filter: 'grayscale(100%)', flexShrink: 0 }} />
                        <div>
                          <div style={{ fontFamily: font.mono, fontSize: '0.7rem', color: '#fff' }}>@{u.login}</div>
                          <div style={{ fontFamily: font.body, fontSize: '0.72rem', color: '#444' }}>{u.display_name}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...S.td, fontFamily: font.mono, fontSize: '0.62rem', color: '#444' }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ ...S.td, fontFamily: font.mono, fontSize: '0.65rem', color: '#555' }}>
                      {u.view_count?.toLocaleString() || '0'}
                    </td>
                    <td style={S.td}>
                      {u.is_admin    && <span style={S.pill('#1a0000','#ff4444')}>ADMIN</span>}
                      {u.is_beta     && <span style={S.pill('#0d1a0d','#4caf50')}>BETA</span>}
                      {u.is_verified && <span style={S.pill('#0d0d0d','#fff')}>VERIFIED</span>}
                      {u.is_banned   && <span style={S.pill('#1a0000','#ff6b6b')}>BANNED</span>}
                      {!u.is_admin && !u.is_verified && !u.is_banned && !u.is_beta && <span style={{ fontFamily: font.mono, fontSize: '0.58rem', color: '#333' }}>MEMBER</span>}
                    </td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {u.is_beta
                          ? <button style={S.btn} onClick={() => action(`/api/admin/users/${u.twitch_id}/unbeta`, 'Removed beta')}><FlaskConical size={10} />Unbeta</button>
                          : <button style={{ ...S.btn, borderColor: '#0d1a0d', color: '#4caf50' }} onClick={() => action(`/api/admin/users/${u.twitch_id}/beta`, '✓ Beta approved')}><FlaskConical size={10} />Beta</button>
                        }
                        {!u.is_admin && (<>
                          {u.is_verified
                            ? <button style={S.btn} onClick={() => action(`/api/admin/users/${u.twitch_id}/unverify`, 'Unverified')}><ShieldOff size={10} />Unverify</button>
                            : <button style={{ ...S.btn, borderColor: '#1a2a1a', color: '#4caf50' }} onClick={() => action(`/api/admin/users/${u.twitch_id}/verify`, '✓ Verified')}><ShieldCheck size={10} />Verify</button>
                          }
                          {u.is_banned
                            ? <button style={{ ...S.btn, borderColor: '#1a2a1a', color: '#4caf50' }} onClick={() => action(`/api/admin/users/${u.twitch_id}/unban`, 'Unbanned')}><CheckCircle size={10} />Unban</button>
                            : <button style={{ ...S.btn, borderColor: '#2a1a1a', color: '#ff6b6b' }} onClick={() => action(`/api/admin/users/${u.twitch_id}/ban`, 'Banned')}><Ban size={10} />Ban</button>
                          }
                        </>)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </>
      )}
    </div>
  );
};

export default AdminUsers;
